import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Sparkles, ArrowRight } from "lucide-react";
import { streamChat } from "../lib/api";
import { useLang } from "../lib/i18n";
import VoicePlayButton from "./VoicePlayButton";
import { getStoredProfile } from "./ChildProfileModal";

function sid() {
  let s = sessionStorage.getItem("aurelia_sid");
  if (!s) { s = "p-" + Math.random().toString(36).slice(2, 10); sessionStorage.setItem("aurelia_sid", s); }
  return s;
}

export default function HomeChat() {
  const { t, lang } = useLang();
  const [input, setInput] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(sid());

  const suggestions = [1, 2, 3].map((i) => t(`home.chat.sug.${i}`));

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setQuestion(msg);
    setAnswer("");
    setBusy(true);
    await streamChat({
      sessionId: sessionId.current,
      message: msg,
      lang,
      childProfile: getStoredProfile(),
      onDelta: (chunk) => setAnswer((prev) => prev + chunk),
      onDone: () => setBusy(false),
      onError: () => { setAnswer(t("ask.error")); setBusy(false); },
    });
  };

  return (
    <section
      className="relative overflow-hidden rounded-3xl bg-white border border-[#E8E6E1] p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid="home-chat"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3D405B] text-white text-xs font-semibold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> {t("home.chat.badge")}
          </div>
          <h2 className="font-heading text-2xl md:text-3xl text-[#3D405B] mt-3">{t("home.chat.title")}</h2>
          <p className="text-[#8A8D91] text-sm mt-1">{t("home.chat.subtitle")}</p>
        </div>
        <Link
          to="/ask"
          data-testid="home-chat-open-full"
          className="text-sm text-[#3D405B] hover:text-[#E07A5F] font-medium inline-flex items-center gap-1 transition-colors"
        >
          {t("home.chat.openFull")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-5 flex gap-2" data-testid="home-chat-form">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("ask.input.placeholder")}
          data-testid="home-chat-input"
          disabled={busy}
          className="flex-1 bg-[#FDFBF7] border border-[#E8E6E1] rounded-full px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="home-chat-send"
          className="bg-[#E07A5F] text-white rounded-full px-5 py-3 font-medium hover:bg-[#D56D52] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Send className="w-4 h-4" /> <span className="hidden sm:inline">{t("ask.send")}</span>
        </button>
      </form>

      {!question && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="home-chat-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => send(s)}
              data-testid={`home-chat-suggestion-${i}`}
              className="text-xs px-3 py-1.5 rounded-full border border-[#E8E6E1] bg-white hover:border-[#81B29A] hover:bg-[#81B29A]/10 text-[#3D405B] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {question && (
        <div className="mt-5 space-y-3" data-testid="home-chat-conversation">
          <div className="flex justify-end">
            <div className="max-w-[85%] bg-[#3D405B] text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm">
              {question}
            </div>
          </div>
          <div className="flex justify-start">
            <div className="max-w-[95%] bg-[#F4F1DE] text-[#3D405B] rounded-2xl rounded-bl-md px-4 py-3 leading-relaxed whitespace-pre-wrap">
              {answer || (busy && <span className="opacity-60">{t("ask.thinking")}</span>)}
            </div>
          </div>
          {!busy && answer && (
            <div className="text-right">
              <Link
                to="/ask"
                data-testid="home-chat-continue"
                className="text-xs text-[#3D405B] hover:text-[#E07A5F] font-medium inline-flex items-center gap-1"
              >
                {t("home.chat.continue")} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
